const WP_API_BASE = 'https://trust-job.com/wp-json/wp/v2';

export interface WordPressJob {
  id: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  featured_media: number;
  acf: {
    location: string;
    station: string;
    work_point: string;
    salary: string;
    work_time: string;
    holiday: string;
    benefits: string;
    qualification: string;
    application: string;
    facility_type?: string;
    address?: string;
    image?: string;
    images?: Array<{ url: string; alt?: string }>;
  };
}

export interface WordPressMedia {
  id: number;
  source_url: string;
  alt_text: string;
}

// 派遣スタッフの声の型定義
export interface WordPressStaffPost {
  id: number;
  title: {
    rendered: string;
  };
  acf: {
    staff_image: string | { url?: string; sizes?: { medium?: string; large?: string } };
    staff_profile: string;
    staff_schedule: string;
    staff_hourly_rate: string;
    staff_monthly_income: string;
    staff_real_voice: string;
  };
}

export interface StaffVoiceItem {
  id: number;
  title: string;
  image: string;
  profile: string;
  schedule: string;
  hourlyRate: string;
  monthlyIncome: string;
  realVoice: string;
}

export async function fetchWordPressJob(jobId: number): Promise<WordPressJob | null> {
  try {
    const response = await fetch(`${WP_API_BASE}/jobs/${jobId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`求人ID ${jobId} の取得に失敗:`, error);
    return null;
  }
}

export async function fetchFeaturedImageUrl(mediaId: number): Promise<string | null> {
  if (!mediaId) return null;
  try {
    const response = await fetch(`${WP_API_BASE}/media/${mediaId}`);
    if (!response.ok) return null;
    const media: WordPressMedia = await response.json();
    return media.source_url || null;
  } catch (error) {
    console.error(`メディアID ${mediaId} の取得に失敗:`, error);
    return null;
  }
}

export async function fetchFeaturedJobs(jobIds: number[]): Promise<Array<{
  id: number;
  title: string;
  location: string;
  salary: string;
  imageUrl: string;
  raw: WordPressJob;
}>> {
  const results = await Promise.all(
    jobIds.map(async (jobId) => {
      const job = await fetchWordPressJob(jobId);
      if (!job) return null;
      const imageUrl = await fetchFeaturedImageUrl(job.featured_media);
      return {
        id: job.id,
        title: job.title?.rendered || '',
        location: job.acf?.location || '',
        salary: job.acf?.salary || '',
        imageUrl: imageUrl || '',
        raw: job,
      };
    })
  );
  return results.filter((r): r is NonNullable<typeof r> => r !== null);
}

// staff_ フィールドを複数の場所からフォールバック検索するヘルパー
function extractStaffField(post: Record<string, unknown>, fieldName: string): string {
  const acf = post.acf as Record<string, unknown> | undefined;
  const meta = post.meta as Record<string, unknown> | undefined;

  // 1. acf オブジェクト直下
  if (acf && acf[fieldName] !== undefined && acf[fieldName] !== null && acf[fieldName] !== '') {
    return String(acf[fieldName]);
  }
  // 2. meta オブジェクト直下
  if (meta && meta[fieldName] !== undefined && meta[fieldName] !== null && meta[fieldName] !== '') {
    return String(meta[fieldName]);
  }
  // 3. 投稿オブジェクト直下
  if (post[fieldName] !== undefined && post[fieldName] !== null && post[fieldName] !== '') {
    return String(post[fieldName]);
  }
  return '';
}

// staff_image フィールドからURLを取得するヘルパー（複数の場所をフォールバック）
function resolveStaffImageUrl(imageField: string | { url?: string; sizes?: { medium?: string; large?: string } } | undefined): string {
  if (!imageField) return '';
  if (typeof imageField === 'string') return imageField;
  if (typeof imageField === 'object') {
    return imageField.url || imageField.sizes?.large || imageField.sizes?.medium || '';
  }
  return '';
}

function extractStaffImageField(post: Record<string, unknown>): string {
  const acf = post.acf as Record<string, unknown> | undefined;
  const meta = post.meta as Record<string, unknown> | undefined;

  const fromAcf = acf?.['staff_image'];
  if (fromAcf) return resolveStaffImageUrl(fromAcf as string | { url?: string; sizes?: { medium?: string; large?: string } });

  const fromMeta = meta?.['staff_image'];
  if (fromMeta) return resolveStaffImageUrl(fromMeta as string | { url?: string; sizes?: { medium?: string; large?: string } });

  const fromPost = post['staff_image'];
  if (fromPost) return resolveStaffImageUrl(fromPost as string | { url?: string; sizes?: { medium?: string; large?: string } });

  return '';
}

function mapPostToStaffVoiceItem(post: Record<string, unknown>): StaffVoiceItem {
  const acf = post.acf as Record<string, unknown> | undefined;

  // acf直下を最優先で取得、なければフォールバック
  const getField = (fieldName: string): string => {
    if (acf && acf[fieldName] !== undefined && acf[fieldName] !== null && acf[fieldName] !== false && acf[fieldName] !== '') {
      return String(acf[fieldName]);
    }
    return extractStaffField(post, fieldName);
  };

  // staff_image: acf直下を最優先
  let imageUrl = '';
  if (acf && acf['staff_image']) {
    imageUrl = resolveStaffImageUrl(acf['staff_image'] as string | { url?: string; sizes?: { medium?: string; large?: string } });
  }
  if (!imageUrl) {
    imageUrl = extractStaffImageField(post);
  }

  return {
    id: post.id as number,
    title: (post.title as { rendered?: string })?.rendered || '',
    image: imageUrl,
    profile: getField('staff_profile'),
    schedule: getField('staff_schedule'),
    hourlyRate: getField('staff_hourly_rate'),
    monthlyIncome: getField('staff_monthly_income'),
    realVoice: getField('staff_real_voice'),
  };
}

// 派遣スタッフの声：デバッグ用・生のAPIレスポンスを返す
export async function fetchStaffVoicesRaw(postIds: number[]): Promise<Record<number, unknown>> {
  const result: Record<number, unknown> = {};

  // まず posts エンドポイントで一括取得を試みる（acf_format=standard + _embed）
  try {
    const bulkUrl = `${WP_API_BASE}/posts?include=${postIds.join(',')}&acf_format=standard&_embed&per_page=100`;
    const bulkRes = await fetch(bulkUrl);
    if (bulkRes.ok) {
      const posts: unknown[] = await bulkRes.json();
      if (Array.isArray(posts) && posts.length > 0) {
        posts.forEach((p) => {
          const post = p as { id: number };
          result[post.id] = { ...(p as object), _fetchedFrom: 'posts(bulk)' };
        });
        const fetchedIds = Object.keys(result).map(Number);
        const missingIds = postIds.filter((id) => !fetchedIds.includes(id));
        if (missingIds.length === 0) return result;
      }
    }
  } catch {
    // 一括取得失敗時は個別取得へ
  }

  // 個別取得（posts → カスタム投稿タイプ の順で試みる）
  const ENDPOINTS_TO_TRY = ['posts', 'staff', 'staff-voice', 'staffvoice'];

  await Promise.all(
    postIds.map(async (postId) => {
      if (result[postId]) return;

      for (const endpoint of ENDPOINTS_TO_TRY) {
        try {
          const url = `${WP_API_BASE}/${endpoint}/${postId}?acf_format=standard&_embed`;
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            result[postId] = { ...data, _fetchedFrom: endpoint };
            break;
          }
        } catch {
          // 次のエンドポイントを試す
        }
      }

      if (!result[postId]) {
        result[postId] = null;
      }
    })
  );

  return result;
}

// 派遣スタッフの声：指定IDの投稿を取得
export async function fetchStaffVoices(postIds: number[]): Promise<StaffVoiceItem[]> {
  // まず posts エンドポイントで一括取得を試みる
  try {
    const bulkUrl = `${WP_API_BASE}/posts?include=${postIds.join(',')}&acf_format=standard&_embed&per_page=100`;
    const bulkRes = await fetch(bulkUrl);
    if (bulkRes.ok) {
      const posts: unknown[] = await bulkRes.json();
      if (Array.isArray(posts) && posts.length > 0) {
        const mapped = (posts as Record<string, unknown>[]).map(mapPostToStaffVoiceItem);
        if (mapped.length === postIds.length) return mapped;
      }
    }
  } catch {
    // 一括取得失敗時は個別取得へ
  }

  const ENDPOINTS_TO_TRY = ['posts', 'staff', 'staff-voice', 'staffvoice'];

  const results = await Promise.all(
    postIds.map(async (postId) => {
      for (const endpoint of ENDPOINTS_TO_TRY) {
        try {
          const url = `${WP_API_BASE}/${endpoint}/${postId}?acf_format=standard&_embed`;
          const response = await fetch(url);
          if (response.ok) {
            const post = await response.json() as Record<string, unknown>;
            return mapPostToStaffVoiceItem(post);
          }
        } catch {
          // 次のエンドポイントを試す
        }
      }
      return null;
    })
  );

  return results.filter((r): r is NonNullable<typeof r> => r !== null);
}

// 派遣スタッフの声：カテゴリー内の全投稿を取得
export async function fetchAllStaffVoices(categoryId: number): Promise<StaffVoiceItem[]> {
  try {
    const response = await fetch(
      `${WP_API_BASE}/posts?categories=${categoryId}&per_page=100&acf_format=standard&_embed&v=2`
    );
    if (!response.ok) {
      console.warn(`カテゴリID ${categoryId} のAPIレスポンスエラー: ${response.status}`);
      return [];
    }
    const posts: unknown[] = await response.json();
    return (posts as Record<string, unknown>[]).map(mapPostToStaffVoiceItem);
  } catch (error) {
    console.error(`カテゴリID ${categoryId} のスタッフ取得に失敗:`, error);
    return [];
  }
}

// FAQ関連の型定義
export interface WordPressFAQPost {
  id: number;
  title: {
    rendered: string;
  };
  acf: {
    faq_answer: string;
  };
  content: {
    rendered: string;
  };
  categories: number[];
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface FAQItem {
  id: number;
  question: string;
  answer: string;
  categoryId: number;
}

export interface FAQCategory {
  id: number;
  name: string;
  items: FAQItem[];
}

const FAQ_CATEGORY_NAMES = [
  '登録・費用について',
  '働き方について',
  '派遣の仕組みについて',
  'サポートについて',
  '正社員について',
];

async function fetchCategories(): Promise<WordPressCategory[]> {
  try {
    const response = await fetch(`${WP_API_BASE}/categories?per_page=100`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('カテゴリの取得に失敗:', error);
    return [];
  }
}

async function fetchPostsByCategory(categoryId: number): Promise<WordPressFAQPost[]> {
  try {
    const url = `${WP_API_BASE}/posts?categories=${categoryId}&per_page=100&acf_format=standard`;
    const response = await fetch(url);
    if (!response.ok) return [];
    const posts: WordPressFAQPost[] = await response.json();
    return posts;
  } catch (error) {
    console.error(`カテゴリID ${categoryId} の投稿取得に失敗:`, error);
    return [];
  }
}

// HTMLタグを除去する関数
function stripHtmlTags(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export async function fetchFAQData(): Promise<FAQCategory[]> {
  const allCategories = await fetchCategories();

  const faqCategories = FAQ_CATEGORY_NAMES
    .map((name) => allCategories.find((cat) => cat.name === name))
    .filter((cat): cat is WordPressCategory => cat !== undefined);

  if (faqCategories.length === 0) {
    console.warn('FAQ対象カテゴリが見つかりませんでした');
    return [];
  }

  const results = await Promise.all(
    faqCategories.map(async (cat) => {
      const posts = await fetchPostsByCategory(cat.id);
      const items: FAQItem[] = posts.map((post) => {
        // ACFフィールドから回答を取得、なければcontent.renderedから取得
        let answer = post.acf?.faq_answer || '';
        if (!answer && post.content?.rendered) {
          answer = stripHtmlTags(post.content.rendered);
        }

        return {
          id: post.id,
          question: post.title.rendered
            .replace(/&amp;/g, '&')
            .replace(/&#8217;/g, "'")
            .replace(/&#8220;/g, '"')
            .replace(/&#8221;/g, '"')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>'),
          answer: answer.trim(),
          categoryId: cat.id,
        };
      });

      return {
        id: cat.id,
        name: cat.name,
        items,
      };
    })
  );

  return results;
}